import { useAuth } from '@/contexts/AuthContext'
import { CompanyUserRole } from '@/types/companyUser'
import { UserType } from '@/types/user'

export interface CompanyAccessInfo {
  isCompanyDisabled: boolean
  isOwner: boolean
  canAccessSidebar: boolean
  canCreatePosts: boolean
  allowedSidebarItems: string[]
  disabledMessage: string
}

export function useCompanyAccess(): CompanyAccessInfo {
  const { company, userData, userRole } = useAuth()

  const isCompanyDisabled = company?.status === false
  const isOwner = userRole === CompanyUserRole.ADMIN || userData?.userType === UserType.ADMIN
  const isSuperAdmin = userData?.userType === UserType.SUPER_ADMIN || userData?.isRoot

  // If user has no company (e.g., after ownership transfer)
  if (!company && !isSuperAdmin) {
    return {
      isCompanyDisabled: false,
      isOwner: false,
      canAccessSidebar: true,
      canCreatePosts: false,
      allowedSidebarItems: ['dashboard', 'companies', 'privacy-policy', 'support'],
      disabledMessage: '',
    }
  }

  // If company is disabled
  if (isCompanyDisabled) {
    // Super admins can access everything except posts
    if (isSuperAdmin) {
      return {
        isCompanyDisabled,
        isOwner: false,
        canAccessSidebar: true,
        canCreatePosts: false,
        allowedSidebarItems: ['dashboard', 'companies', 'pr-members', 'media-members', 'privacy-policy', 'support'],
        disabledMessage: '',
      }
    }

    // Company owners can only access help & support and privacy policy
    if (isOwner) {
      return {
        isCompanyDisabled,
        isOwner,
        canAccessSidebar: true,
        canCreatePosts: false,
        allowedSidebarItems: ['privacy-policy', 'support'],
        disabledMessage:
          'This company is currently disabled. Only Help & Support and Privacy Policy are available. Please contact support for assistance.',
      }
    }

    // All other users cannot access anything
    return {
      isCompanyDisabled,
      isOwner: false,
      canAccessSidebar: false,
      canCreatePosts: false,
      allowedSidebarItems: [],
      disabledMessage: 'This company is currently disabled. Please contact your PR manager for assistance.',
    }
  }

  // Company is active - normal access
  // Super admins still don't get posts in sidebar
  if (isSuperAdmin) {
    return {
      isCompanyDisabled: false,
      isOwner: false,
      canAccessSidebar: true,
      canCreatePosts: false,
      allowedSidebarItems: ['dashboard', 'companies', 'pr-members', 'media-members', 'privacy-policy', 'support'],
      disabledMessage: '',
    }
  }

  // Regular users with company access
  return {
    isCompanyDisabled: false,
    isOwner,
    canAccessSidebar: true,
    canCreatePosts: true,
    allowedSidebarItems: [
      'dashboard',
      'posts',
      'companies',
      'pr-members',
      'media-members',
      'privacy-policy',
      'support',
    ],
    disabledMessage: '',
  }
}
